import { useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import { FILTER_TYPES } from 'constants/filterType';

const useUrlParams = (
  filterOptions,
  initialPageSize = 25,
  initialFilters = {},
  enablePagination = true,
) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const isValidValue = useCallback(
    value => value !== undefined && value !== null && value !== '',
    [],
  );

  const processFilterValue = useCallback(
    (field, value, params, isMultiSelect = false) => {
      if (Array.isArray(value)) {
        value.forEach(v => {
          if (isValidValue(v)) {
            isMultiSelect ? params.append(field, v) : params.set(field, v);
          }
        });
      } else if (isValidValue(value)) {
        params.set(field, value);
      }
    },
    [isValidValue],
  );

  const getCurrentValues = useCallback(() => {
    const pageIndex = parseInt(searchParams.get('page') || '1') - 1;
    const pageSize = parseInt(searchParams.get('page_size')) || initialPageSize;

    const sortParams = searchParams.getAll('sort');
    const sorting =
      sortParams.length > 0
        ? sortParams.map(param => {
            const isDesc = param.startsWith('-');
            return {
              id: isDesc ? param.slice(1) : param,
              desc: isDesc,
            };
          })
        : [];

    if (!searchParams.toString()) {
      return { pageIndex, pageSize, filters: initialFilters, sorting };
    }

    const filters = {};

    const multiValueFields =
      filterOptions
        ?.filter(
          opt =>
            opt.type === FILTER_TYPES.MULTI_SELECT ||
            opt.type === FILTER_TYPES.MULTI_TEXT,
        )
        .map(opt => opt.field) || [];

    searchParams.forEach((value, field) => {
      if (
        field !== 'page' &&
        field !== 'page_size' &&
        field !== 'sort' &&
        isValidValue(value)
      ) {
        if (multiValueFields.includes(field)) {
          if (!filters[field]) {
            const filterOption = filterOptions?.find(
              opt => opt.field === field,
            );
            if (filterOption?.type === FILTER_TYPES.MULTI_TEXT) {
              filters[field] = searchParams.getAll(field).join('\n');
            } else {
              filters[field] = searchParams.getAll(field);
            }
          }
        } else {
          filters[field] = value;
        }
      }
    });

    return { pageIndex, pageSize, filters, sorting };
  }, [
    searchParams,
    initialPageSize,
    initialFilters,
    filterOptions,
    isValidValue,
  ]);

  const updateParams = useCallback(
    ({ pageIndex, pageSize, filters, sorting = [] }) => {
      const newParams = new URLSearchParams();

      if (enablePagination) {
        if (pageIndex !== undefined)
          newParams.set('page', (pageIndex + 1).toString());
        if (pageSize !== undefined)
          newParams.set('page_size', pageSize.toString());
      }

      if (filters) {
        Object.entries(filters).forEach(([field, value]) => {
          const filterOption = filterOptions?.find(opt => opt.field === field);

          if (filterOption?.type === FILTER_TYPES.MULTI_TEXT) {
            const values = value
              .split('\n')
              .map(item => item.trim())
              .filter(item => item !== '');

            processFilterValue(field, values, newParams, true);
          } else {
            const isMultiValueType =
              filterOption?.type === FILTER_TYPES.MULTI_SELECT;

            processFilterValue(field, value, newParams, isMultiValueType);
          }
        });
      }

      // Sorting
      if (Array.isArray(sorting) && sorting.length > 0) {
        sorting.forEach(({ id, desc }) => {
          newParams.append('sort', desc ? `-${id}` : id);
        });
      }

      setSearchParams(newParams);
    },
    [enablePagination, filterOptions, processFilterValue, setSearchParams],
  );

  return useMemo(
    () => ({
      ...getCurrentValues(),
      updateParams,
    }),
    [getCurrentValues, updateParams],
  );
};

export default useUrlParams;
